import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { generatePath, useNavigate } from 'react-router-dom';
import { useQuery } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import {
    LastUpdateTimestampAndRefresh,
    LastUpdateTimestampAndRefreshAsyncLoadedSection,
} from '../ui/LastUpdateTimestampAndRefresh.tsx';
import {
    DetailHeader,
    DetailTabs,
    ParkingaboUserInfoTab,
    useParkingaboUserDetailOutletContext,
    HeaderBarWithRefreshButton,
} from './OperatorParkingaboUsersDetail.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { FetchError } from '../shared-mui-components/ListMessages.tsx';
import {
    OffstreetTransactionsList,
    OffstreetTransactionsOutlet,
    OffstreetTransactionsSearchResult,
    TransactionType,
} from '../offstreet-transactions/OperatorOffstreetTransactionsRoute.tsx';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { isRouteAllowed } from '../app/components/OperatorRoutes.tsx';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { OperatorAppRoutes } from '../app/config/OperatorRoutingConfig.tsx';
import { ParkingaboCustomerPaymentMode } from 'product-shared/parkingabo/ParkingaboOperatingMode.tsx';

export function OperatorParkingaboUsersTransactions() {
    const { parkingaboUser } = useParkingaboUserDetailOutletContext();
    const { operatorData, currentLogin } = useOperatorContext();

    const [transactionsState, refetchTransactionList] = useServerFetch<
        OffstreetTransactionsSearchResult,
        { customerNr: string },
        null
    >(
        context => ({
            url: `/ui-api/operator-account/parkingabo/customer/${context.customerNr}/transactions`,
        }),
        { customerNr: parkingaboUser.customerNr },
    );

    const query = useQuery();
    const navigate = useNavigate();

    return (
        <OperatorRoutedModalContent
            header={<DetailHeader />}
            bar={
                <DetailTabs
                    parkingaboUserInfoTab={ParkingaboUserInfoTab.TRANSACTIONS}
                    roleHasAppPayment={
                        parkingaboUser.role.paymentMode ===
                        ParkingaboCustomerPaymentMode.APP_PAYMENT
                    }
                />
            }
            style={{ padding: '0px 0px' }}
            body={
                <>
                    <HeaderBarWithRefreshButton
                        refreshButton={
                            <LastUpdateTimestampAndRefreshAsyncLoadedSection
                                requestState={transactionsState}
                                onRefresh={refetchTransactionList}
                                render={(trans, timestamp) => (
                                    <LastUpdateTimestampAndRefresh
                                        resultsCount={trans.results.length}
                                        eof={trans.eof}
                                        onRefresh={refetchTransactionList}
                                        timestamp={timestamp}
                                    />
                                )}
                            />
                        }
                    />
                    <OperatorAsyncLoadedSection
                        requestState={transactionsState}
                        pendingLoaderPreset={
                            LoadingSpinnerPresets.FillAllSpaceAndCenter
                        }
                        renderError={() => (
                            <FetchError refetchList={refetchTransactionList} />
                        )}
                        render={data => {
                            return (
                                <OffstreetTransactionsList
                                    data={data}
                                    onRowClick={
                                        isRouteAllowed(
                                            operatorData,
                                            currentLogin,
                                            OperatorAppRoutes.OffstreetTransactions,
                                        )
                                            ? (
                                                  transactionType: TransactionType,
                                                  id: string,
                                              ) => {
                                                  navigate(
                                                      `${generatePath(
                                                          `${getRouteForTransactionType(
                                                              transactionType,
                                                          )}/:id/detail`,
                                                          {
                                                              id: id,
                                                          },
                                                      )}?${query}`,
                                                  );
                                              }
                                            : () =>
                                                  navigate(`no-access?${query}`)
                                    }
                                    isParkingaboCustomerView
                                />
                            );
                        }}
                    />
                    <OffstreetTransactionsOutlet
                        context={{
                            refetchList: refetchTransactionList,
                        }}
                    />
                </>
            }
        />
    );
}

export function getRouteForTransactionType(type: TransactionType): string {
    switch (type) {
        case TransactionType.PENDING_TICKET:
        case TransactionType.PAID_TICKET:
            throw new Error('Not implemented');
        case TransactionType.TICKET_LOST:
            throw new Error('Not implemented');
        case TransactionType.CONTRACT:
            return 'contract';
        case TransactionType.ENTRY_WITHOUT_EXIT:
            return 'entry-without-exit';
    }
}
